/*
 * PRAVILO JEDINICA
 *
 *   rem   font-size, padding, margin, gap
 *         Sve sto mora pratiti gustocu sucelja. Racuna se od html { font-size }
 *         u index.css, pa je ta jedna vrijednost ručica za cijelu aplikaciju.
 *
 *   px    obrubi, border-radius, fiksne sirine i visine
 *         Sve sto NE smije skalirati. Hairline od 1px mora ostati 1px na svakoj
 *         gustoci, inace nestane ili podeblja.
 *
 *   px    prijelomne tocke u @media
 *         Nije stvar ukusa: rem u media upitu ignorira html i uvijek se racuna
 *         od pocetne velicine preglednika. U rem bi upiti prestali odgovarati
 *         ostatku stilova cim se osnovica promijeni.
 *
 * Prije je bilo izmijesano - font pola u px pola u rem, razmaci gotovo svi u px.
 * Zbog toga smanjivanje osnovice nije davalo gusce sucelje nego krivo: manji
 * tekst u jednako velikim karticama.
 */
const theme = {
    colors: {
        // General Colors
        brand: 'rgba(130, 22, 146, 1)', // Primary brand color
        brandLight: 'rgba(130, 22, 146, 0.8)', // Light variant of brand color
        brandLighter: 'rgba(130, 22, 146, 0.2)', // Lighter variant
        brandBackground: 'rgba(130, 22, 146, 0.1)', // Background variant
        brandBackgroundSoft: 'rgba(130, 22, 146, 0.05)',
        textPrimary: '#333', // Primary text color
        textSecondary: '#666', // Secondary text color
        textMuted: '#6b7280',
        textInverse: '#ffffff',

        // Semantic surfaces and borders
        surface: '#ffffff',
        surfaceAlt: '#f9fafb',
        surfaceMuted: '#f3f4f6',
        pageBackground: '#f5f5f5',
        border: '#e5e7eb',
        borderStrong: '#d1d5db',
        divider: '#e5e7eb',

        // Shell / navigation
        sidebarBg: '#ffffff',
        topbarBg: '#ffffff',
        navText: '#4b5563',
        navIconMuted: '#9ca3af',
        navItemBg: 'transparent',
        navItemActiveBgHover: 'rgba(130, 22, 146, 0.12)',
        tooltipBg: '#374151',

        // Tables
        tableHeaderBg: '#f9fafb',
        tableRowHoverBg: '#f8fafc',

        // Status semantic colors
        successBg: '#ecfdf5',
        warningBg: '#fffbeb',
        dangerBg: '#fef2f2',
        infoBg: '#eff6ff',

    // Specific Context Colors (from Dashboard.js icons)
    revenue: '#4caf50', // Average revenue per user (growth/success)
    transactionValue: '#ff9800', // Average selling transaction value (energy/progress)
    belongingUsers: '#18de15', // Belonging users count (new/activity)
    campaigns: '#9c27b0', // Campaigns count (innovation)
    manufacturer: '#f44336', // Manufacturer count (warnings/manufacturers)
    revenueValue: '#3f51b5', // Revenue value (reliability)
    transactions: '#ff5722', // Selling transactions count (actions)
    soldItems: '#795548', // Sold items count (stability/inventory)
    totalVolume: '#8bc34a', // Total transaction volume (freshness/volume)
    users: '#2196f3', // Users count (calmness/users)
    venues: '#607d8b', // Venues count (neutrality/venues)
    neutral: '#9e9e9e', // Default fallback color

    // Bootstrap-like Variants (from Statistics.js colors)
    primary: '#007bff', // General primary actions/metrics
    success: '#28a745', // Success states (e.g., total buyers own)
    info: '#17a2b8', // Info states (e.g., total buyers global)
    warning: '#ffc107', // Warning states (e.g., total revenue own)
    danger: '#dc3545', // Danger states (e.g., total revenue global)
    grey: '#f3f4f6',
    },
    /*
     * Razmaci su u rem iz istog razloga kao i tipografija: da ih rucica na html
     * pomakne zajedno s tekstom. Dok su bili u px, smanjivanje osnovice davalo
     * je manji tekst u jednako velikim karticama - prozracnije umjesto guste,
     * dakle obrnuto od trazenog.
     *
     * U px ostaju samo stvari koje NE smiju skalirati: obrubi od 1px, radijusi,
     * i prijelomne tocke - rem u media upitu ionako ignorira html i racuna se od
     * pocetne velicine preglednika.
     */
    spacing: {
        xs:     '0.25rem',  // 4px
        sm:     '0.5rem',   // 8px
        md:     '0.75rem',  // 12px
        lg:     '1rem',     // 16px
        xl:     '1.5rem',   // 24px
        xxl:    '2rem',     // 32px

        // Nasljedjeni nazivi; iste vrijednosti kao prije, samo u rem.
        small:  '0.625rem', // 10px
        medium: '1.25rem',  // 20px
        large:  '2.5rem',   // 40px
    },
    /*
     * Sedam koraka. Pokrivaju 87% od 635 deklaracija koje su prije stajale kao
     * 32 razlicite vrijednosti - sest njih izmedju 12 i 13,6px, cetiri izmedju
     * 14 i 15,2. Te se razlike ne vide, ali ih je svatko morao pamtiti.
     *
     * Sve u rem, i osnovica je 16px na html. Zato je gustoca cijele aplikacije
     * jedan redak: html { font-size: 0.9375rem } smanjuje sve odjednom. Dok su
     * velicine bile pola u px pola u rem, ta ručica nije postojala - pomaknula
     * bi polovicu teksta i ostavila drugu.
     */
    fontSizes: {
        label:   '0.6875rem',  // 0.6875rem  oznake, badgevi, natpisi nad poljima
        meta:    '0.75rem',    // 0.75rem  pomocni tekst, tablicna meta
        small:   '0.875rem',   // 0.875rem  gusti sadrzaj; zove ga 12 mjesta, bilo 14,4px
        dense:   '0.8125rem',  // 13px  redovi tablica, guste liste
        base:    '0.875rem',   // 14px  osnovni tekst sucelja
        lead:    '1rem',       // 16px  istaknuti tekst, veliki gumbi
        title:   '1.25rem',    // 20px  naslov sekcije
        heading: '1.5rem',     // 24px  naslov kartice, veliki broj
        display: '1.75rem',    // 28px  naslov stranice

        /*
         * Nasljedjeni nazivi, koje zove 34 mjesta. Preslikani su na najblizi
         * korak koji ih NE pomice vidljivo - skala se uvodi da bi se moglo
         * smanjivati, a ne da bi se smanjilo usput i neprimjetno.
         *
         * Jedini koji se stvarno mijenja je medium: 17,6 -> 16px. Ostali padaju
         * na isti ili poludupli piksel od onoga sto su vec bili.
         */
        extraSmall: '0.75rem',        // 12px  bilo 12px
        extraSmallMobile: '0.6875rem', // 11px  bilo 10,4px - ispod donje granice
        medium: '1rem',               // 16px  bilo 17,6px
        large: '1.5rem',              // 24px  bilo 24px
        extraLarge: '1.75rem',        // 28px  bilo 28,8px
    },
    /*
     * Ikone su vec bile najurednije u aplikaciji - drzale su se 18/20/24/28 bez
     * da je to igdje pisalo. Sada pise.
     */
    iconSizes: {
        sm: 16,
        md: 18,   // navigacija, alatne trake
        lg: 20,   // radnje u tablicama
        xl: 24,   // naslovi sekcija
    },
    /*
     * Bootstrap donosi svoje tocke na .98px, a rucno pisane su stajale jedan
     * piksel dalje - na tocno 768px mogla su se aktivirati dva pravila.
     * Ove prate Bootstrap, da se ne sudaraju.
     */
    breakpoints: {
        xs: '575.98px',
        mobile: '767.98px',
        tablet: '991.98px',
        desktop: '1199.98px',
        wide: '1399.98px',
    },
    charts: {
        primary: '#821692',
        secondary: '#3f51b5',
        /*
         * Redoslijed boja za grafove s vise serija. Fiksan je - druga serija
         * uvijek dobije drugu boju, nikad se ne vrti u krug.
         *
         * Ne koristi par primary+secondary: njihova razlika za deuteranopiju je
         * dE 5,9, sto znaci da ih otprilike 6% muskaraca vidi kao istu boju.
         * Ljubicasta + narancasta daje dE 25,8 (tritan) i prolazi sve provjere.
         * Kad dodamo tamnu temu, koraci za nju su #a855c4 i #d95926.
         */
        series: ['#821692', '#eb6834'],
        positive: '#22c55e',
        warning: '#f59e0b',
        danger: '#ef4444',
        neutral: '#64748b',
    },

};

export default theme;
